import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import EsgSearchFilter from "@components/esg-guide/EsgSearchFilter";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@common/queryKeys";

const MenuWrapper = styled.div`
  > ul {
    @media (max-width: 991px) {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      grid-column-gap: 10px;
      grid-row-gap: 10px;
      .submenu {
        display: none;
      }
    }
    @media (max-width: 399px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
`;

const Menu = styled.li`
  & a {
    word-break: keep-all;
    display: flex;
    align-items: center;
    height: 100%;
    @media (max-width: 1200px) {
      padding: 15px 10px !important;
    }
    @media (max-width: 991px) {
      width: 100%;
    }
  }
`;

const SubMenu = styled.div`
  padding: 0 0 0 20px;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  & span {
    margin-bottom: 10px;
    border-bottom: 1px solid #ececec;
    cursor: pointer;
    :last-of-type {
      margin-bottom: 0;
    }
  }
  .activeSub {
    color: #3bb77e;
  }
  .noneSub {
    color: #7e7e7e;
  }
`;

const Wrapper = styled.div`
  @media (max-width: 991px) {
    flex-wrap: wrap;
    .dashboard-menu {
      width: 100%;
    }
  }
`;

const pathName = [
  "",
  "certification",
  "news",
  "letter",
  "blog/package",
  "blog/make",
];

const sideMenus = [
  "ESG",
  "포장재 인증정보",
  "ESG 환경뉴스",
  "ESG 뉴스레터",
  "소재와 포장재",
  "제작사례 & 포장재리뷰",
];

export const boardNameMap = {
  1: "certification",
  2: "news",
  3: "letter",
  4: "package",
  5: "make",
};

const EsgLayout = (props) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const routePathName = router.pathname.split("esg-guide/")[1];
  const activeIndex = pathName.indexOf(
    routePathName ? routePathName : router.pathname.split("/")[0]
  );

  // 공통 검색 상태 관리
  const [searchParams, setSearchParams] = useState({
    searchTerm: "",
    page: 1,
  });
  const [page, setPage] = useState(1);
  const pageRows = 10;

  // 현재 활성화된 메뉴에 해당하는 boardName 가져오기
  const getCurrentBoardName = () => {
    return boardNameMap[activeIndex] || "";
  };

  // 페이지 변경 처리
  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams((prev) => ({ ...prev, page: newPage }));
    window.scrollTo(0, 100);
    // 쿼리 무효화 처리
    const boardName = getCurrentBoardName();
    if (boardName) {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ESG.BOARDS.INDEX(
          boardName,
          searchParams.searchTerm,
          newPage,
          pageRows
        ),
      });
    }
  };

  // 검색 처리 로직 - 자식 컴포넌트에 전달
  const handleSearch = (term) => {
    setSearchParams({
      searchTerm: term,
      page: 1,
    });
    setPage(1);

    // 쿼리 무효화 처리
    const boardName = getCurrentBoardName();
    if (boardName) {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ESG.BOARDS.INDEX(boardName, term, 1, pageRows),
      });
    }
  };

  const handleOnClick = (index) => {
    const url = `/esg-guide/${pathName[index]}`;
    router.push(url);

    // 메뉴 변경 시 검색 상태 초기화
    setSearchParams({
      searchTerm: "",
      page: 1,
    });
    setPage(1);
  };

  // 자식 컴포넌트에 전달할 공통 props
  const commonChildProps = {
    searchParams,
    setSearchParams,
    page,
    pageRows,
    boardName: getCurrentBoardName(),
    onPageChange: handlePageChange,
    onSearch: handleSearch,
  };

  // 자식 요소에 props 전달
  const childrenWithProps = React.Children.map(props.children, (child) => {
    // 리액트 요소인지 확인
    if (React.isValidElement(child)) {
      return React.cloneElement(child, commonChildProps);
    }
    return child;
  });
  return (
    <div className="page-content mt-50">
      <div className="container">
        <div className="row">
          <div className="col-lg-12 m-auto">
            <Wrapper className="row">
              {activeIndex !== 0 && (
                <div className="col-md-2 mb-30  dashboard-menu">
                  <MenuWrapper>
                    <ul className="nav flex-column" role="tablist">
                      <Menu className="nav-item">
                        <a
                          className={
                            activeIndex === 0 ? "nav-link active" : "nav-link"
                          }
                          onClick={() => handleOnClick(0)}
                        >
                          <div className="d-flex align-items-center">
                            <i
                              className="fi-rs-hand-holding-heart mr-10"
                              aria-hidden="true"
                            />
                            <span className="font-sm">{sideMenus[0]}</span>
                          </div>
                        </a>
                      </Menu>
                      <Menu className="nav-item">
                        <a
                          className={
                            activeIndex === 1 ? "nav-link active" : "nav-link"
                          }
                          onClick={() => handleOnClick(1)}
                        >
                          <div className="d-flex align-items-center">
                            <i
                              className="fi-rs-asterik mr-10"
                              aria-hidden="true"
                            />

                            <span className="font-sm">{sideMenus[1]}</span>
                          </div>
                        </a>
                      </Menu>
                      <Menu className="nav-item">
                        <a
                          className={
                            activeIndex === 2 ? "nav-link active" : "nav-link"
                          }
                          onClick={() => handleOnClick(2)}
                        >
                          <div className="d-flex align-items-center">
                            <i
                              className="fi-rs-ballot mr-10"
                              aria-hidden="true"
                            />
                            <span className="font-sm">{sideMenus[2]}</span>
                          </div>
                        </a>
                      </Menu>
                      <Menu className="nav-item menu-item-has-children">
                        <a
                          className={
                            activeIndex === 3 ? "nav-link active" : "nav-link"
                          }
                          onClick={() => handleOnClick(3)}
                        >
                          <div className="d-flex align-items-center">
                            <i
                              className="fi-rs-blog-text mr-10"
                              aria-hidden="true"
                            />
                            <span className="font-sm">{sideMenus[3]}</span>
                          </div>
                        </a>
                      </Menu>
                      <Menu className="nav-item menu-item-has-children">
                        <a
                          className={
                            activeIndex === 4 ? "nav-link active" : "nav-link"
                          }
                          onClick={() => handleOnClick(4)}
                        >
                          <div className="d-flex align-items-center">
                            <i
                              className="fi-rs-bold mr-10"
                              aria-hidden="true"
                            />
                            <span className="font-sm">{sideMenus[4]}</span>
                          </div>
                        </a>
                      </Menu>
                      <Menu className="nav-item menu-item-has-children">
                        <a
                          className={
                            activeIndex === 5 ? "nav-link active" : "nav-link"
                          }
                          onClick={() => handleOnClick(5)}
                        >
                          <div className="d-flex align-items-center">
                            <i
                              className="fi-rs-bold mr-10"
                              aria-hidden="true"
                            />
                            <span className="font-sm">{sideMenus[5]}</span>
                          </div>
                        </a>
                      </Menu>
                    </ul>
                  </MenuWrapper>
                </div>
              )}
              {/* 내용 부분 */}
              {activeIndex !== 0 ? (
                <div
                  className="col-lg-10 pl-sm-30"
                  style={{ minHeight: "30rem" }}
                >
                  <div className="mb-30 d-flex align-items-center flex-wrap">
                    <h1
                      style={{
                        margin: 0,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sideMenus[activeIndex]}
                    </h1>
                    {/* 검색 필터 */}
                    {activeIndex !== 1 && (
                      <div
                        className="flex-grow-1"
                        style={{ minWidth: "280px" }}
                      >
                        <EsgSearchFilter
                          searchParams={searchParams}
                          setSearchParams={setSearchParams}
                          boardName={getCurrentBoardName()}
                          page={page}
                          pageRow={pageRows}
                          onPageChange={handlePageChange}
                        />
                      </div>
                    )}
                  </div>

                  {childrenWithProps}
                </div>
              ) : (
                <>{childrenWithProps}</>
              )}
            </Wrapper>
          </div>
        </div>
      </div>
    </div>
  );
};
export default EsgLayout;
